import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  RISK_DYNAMIC_FIELD_CURRENCIES,
  RISK_DYNAMIC_FIELD_OPTION_TYPES,
  RISK_DYNAMIC_FIELD_TYPE_OPTIONS,
  RiskDynamicField,
  RiskDynamicFieldOption,
  RiskDynamicFieldTypeEnum,
} from 'app/risk-register/risk-register.constant';

@Component({
  selector: 'cygov-risk-dynamic-field-panel',
  templateUrl: './risk-dynamic-field-panel.component.html',
  styleUrls: ['./risk-dynamic-field-panel.component.scss'],
  standalone: false,
})
export class RiskDynamicFieldPanelComponent implements OnChanges {
  /** The field being edited, or null to author a new one. */
  @Input() field: RiskDynamicField = null;

  @Input() usageCount = 0;

  /** True while the parent has a write in flight; disables the commit buttons. */
  @Input() isSaving = false;

  @Output() saved = new EventEmitter<RiskDynamicField>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<RiskDynamicField>();

  // Draft state. Held flat rather than as a partial RiskDynamicField so the
  // template binds directly and an abandoned edit never touches the input.
  label = '';
  type: RiskDynamicFieldTypeEnum = null;
  required = false;
  currency: string = null;
  options: RiskDynamicFieldOption[] = [];

  // Option editor state — the tag-box pattern from the Dynamic Label Manager:
  // the `+` reveals an inline add box rather than opening a modal.
  isAddingOption = false;
  newOptionName = '';

  isTypeOpen = false;
  isCurrencyOpen = false;
  /** Set on a failed save so the panel can explain itself instead of doing nothing. */
  errorMessage: string = null;

  readonly typeOptions = RISK_DYNAMIC_FIELD_TYPE_OPTIONS;
  readonly currencyOptions = RISK_DYNAMIC_FIELD_CURRENCIES;
  readonly fieldTypes = RiskDynamicFieldTypeEnum;

  /**
   * Reseeds the draft only when the field itself changes. Keyed on `field` rather
   * than firing for any input, so a future `usageCount` binding can update
   * without discarding whatever the user has typed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field) {
      this.resetDraft();
    }
  }

  get isEditMode(): boolean {
    return !!this.field;
  }

  /** Whether the type in the draft takes an author-defined option list. */
  get needsOptions(): boolean {
    return RISK_DYNAMIC_FIELD_OPTION_TYPES.indexOf(this.type) > -1;
  }

  get needsCurrency(): boolean {
    return this.type === RiskDynamicFieldTypeEnum.CURRENCY;
  }

  get selectedTypeLabel(): string {
    const match = this.typeOptions.find(option => option.id === this.type);
    return match ? match.name : '';
  }

  get selectedCurrencyLabel(): string {
    const match = this.currencyOptions.find(option => option.id === this.currency);
    return match ? match.name : '';
  }

  private resetDraft(): void {
    this.label = this.field ? this.field.label : '';
    this.type = this.field ? this.field.type : null;
    this.required = this.field ? !!this.field.required : false;
    this.currency = this.field ? this.field.currency || null : null;
    // Cloned: editing the list must not mutate the caller's field until save.
    this.options = this.field?.options ? this.field.options.map(option => ({ ...option })) : [];
    this.isAddingOption = false;
    this.newOptionName = '';
    this.isTypeOpen = false;
    this.isCurrencyOpen = false;
    this.errorMessage = null;
  }

  // ---------------------------------------------------------------------------
  // Type / currency pickers
  // ---------------------------------------------------------------------------

  toggleTypeList(): void {
    this.isTypeOpen = !this.isTypeOpen;
    this.isCurrencyOpen = false;
  }

  toggleCurrencyList(): void {
    this.isCurrencyOpen = !this.isCurrencyOpen;
    this.isTypeOpen = false;
  }

  selectType(option: RiskDynamicFieldOption): void {
    const nextType = option.id as RiskDynamicFieldTypeEnum;
    this.isTypeOpen = false;
    if (nextType === this.type) {
      return;
    }
    this.type = nextType;
    this.errorMessage = null;
    // The extra row a type owns is meaningless to the others, so switching away
    // clears it rather than carrying a stale value into the saved definition.
    if (!this.needsOptions) {
      this.options = [];
      this.isAddingOption = false;
      this.newOptionName = '';
    }
    if (!this.needsCurrency) {
      this.currency = null;
    }
  }

  selectCurrency(option: RiskDynamicFieldOption): void {
    this.currency = option.id;
    this.isCurrencyOpen = false;
    this.errorMessage = null;
  }

  /** Closes whichever list is open when the click lands outside the panel's pickers. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isTypeOpen && !this.isCurrencyOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target?.closest?.('.field-picker')) {
      return;
    }
    this.isTypeOpen = false;
    this.isCurrencyOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isTypeOpen = false;
    this.isCurrencyOpen = false;
  }

  // ---------------------------------------------------------------------------
  // Option list (the tag-box behaviour)
  // ---------------------------------------------------------------------------

  showAddOptionBox(): void {
    this.isAddingOption = true;
    this.newOptionName = '';
  }

  closeAddOptionBox(): void {
    this.isAddingOption = false;
    this.newOptionName = '';
  }

  /**
   * Appends the typed name as a new option. Rejects blanks and case-insensitive
   * duplicates, since the option name is what a risk stores as its value.
   */
  addOption(): void {
    const name = (this.newOptionName || '').trim();
    if (!name) {
      return;
    }
    const isDuplicate = this.options.some(option => option.name.toLowerCase() === name.toLowerCase());
    if (isDuplicate) {
      this.errorMessage = `"${name}" is already an option.`;
      return;
    }
    this.options = [...this.options, { id: this.buildOptionId(name), name }];
    this.newOptionName = '';
    this.isAddingOption = false;
    this.errorMessage = null;
  }

  removeOption(target: RiskDynamicFieldOption): void {
    this.options = this.options.filter(option => option.id !== target.id);
  }

  trackByOptionId(index: number, option: RiskDynamicFieldOption): string {
    return option.id;
  }

  /**
   * camelCase id derived from the name, because the wizard's dropdown matches
   * selections on `id`. Suffixed on collision so two names that slug to the same
   * value (e.g. "Data Security" and "data-security") stay addressable.
   */
  private buildOptionId(name: string): string {
    const words = name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => !!word);
    const base =
      words.map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))).join('') ||
      'option';
    let id = base;
    let suffix = 2;
    while (this.options.some(option => option.id === id)) {
      id = `${base}${suffix}`;
      suffix++;
    }
    return id;
  }

  // ---------------------------------------------------------------------------
  // Commit
  // ---------------------------------------------------------------------------

  onSave(): void {
    const label = (this.label || '').trim();
    if (!label) {
      this.errorMessage = 'Field Title is required.';
      return;
    }
    if (!this.type) {
      this.errorMessage = 'Type is required.';
      return;
    }
    if (this.needsOptions && this.options.length < 2) {
      this.errorMessage = 'Add at least two options for this type.';
      return;
    }
    if (this.needsCurrency && !this.currency) {
      this.errorMessage = 'Currency is required.';
      return;
    }

    const definition: RiskDynamicField = {
      // An existing field keeps its key: the wizard stores collected values under
      // it, so re-deriving it from a renamed label would orphan them.
      key: this.field ? this.field.key : this.buildFieldKey(label),
      label,
      type: this.type,
      required: this.required,
    };
    if (this.needsOptions) {
      definition.options = this.options;
    }
    if (this.needsCurrency) {
      definition.currency = this.currency;
    }
    this.errorMessage = null;
    this.saved.emit(definition);
  }

  onCancel(): void {
    this.resetDraft();
    this.cancelled.emit();
  }

  onDelete(): void {
    this.deleted.emit(this.field);
  }

  private buildFieldKey(label: string): string {
    const words = label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => !!word);
    return (
      words.map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))).join('') ||
      'field'
    );
  }
}
